import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bar, Doughnut } from "react-chartjs-2";
import ChartDataLabels from "chartjs-plugin-datalabels";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from "chart.js";
import {
  Leaf,
  Filter,
  Calendar,
  Loader2,
} from "lucide-react";
import Topbar from "Components/Topbar/Topbar";
import { MultiSearchableCombobox } from "Components/MultiSearchableCombobox";
import { usePersistedState } from "../../hooks/utility/usePersistedState";
import { useGetSites } from "../../hooks/site/useGetSites";
import { useGetBUs } from "../../hooks/reference/useGetBUs";
import { useGetActivites } from "../../hooks/reference/useGetActivites";
import { useGetFilieres } from "../../hooks/reference/useGetFilieres";
import { useGetRegions } from "../../hooks/reference/useGetRegions";
import {
  useGetEnergieCarboneEmpreinte,
  useGetElevageCarboneEmpreinte,
  useGetSolCarboneEmpreinte,
  useGetGazFrigoCarboneEmpreinte,
  useGetTransportCarboneEmpreinte,
  useGetAchatCarboneEmpreinte,
  useGetImmobilisationCarboneEmpreinte,
  useGetDechetsCarboneEmpreinte,
  sumSummary,
  getMonthlyTotals,
  classifyEnergieScope,
} from "../../hooks/analytics/useGetCarboneEmpreinte";
import type { CarboneSummaryItem } from "../../hooks/analytics/useGetCarboneEmpreinte";
import type { KpisFilters } from "../../hooks/analytics/useGetKpisAnalytics";
import { useGetCarboneByDimension } from "../../hooks/analytics/useGetCarboneByDimension";
import { legendIsolateClickPie, legendIsolateClickDataset } from "../../Utils/chartLegend";
import type { User as BackendUser } from "../../types/user";
import "./CarbonFootprintPage.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

type ScopeTab = "scope1" | "scope2" | "scope3";

function formatCO2(val: number): string {
  if (val >= 1_000_000) return `${(val / 1_000_000).toFixed(2)}M`;
  if (val >= 1_000) return `${(val / 1_000).toFixed(1)}k`;
  return val.toFixed(1);
}

// ─── Dimension split helpers ──────────────────────────────────────────────────
const SPLIT_COLORS = [
  "#3b82f6", "#f59e0b", "#10b981", "#ec4899", "#8b5cf6",
  "#06b6d4", "#f97316", "#84cc16", "#6366f1", "#14b8a6",
  "#ef4444", "#a855f7", "#22c55e", "#eab308",
];

function makeSplitPieOptions(total: number) {
  return {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "0%",
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: { font: { size: 11 }, padding: 10, boxWidth: 12 },
        onClick: legendIsolateClickPie,
      },
      datalabels: {
        display: (ctx: any): boolean => {
          const val = ctx.dataset.data[ctx.dataIndex] as number;
          return total > 0 && (val / total) * 100 >= 4;
        },
        formatter: (value: number): string => {
          const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
          return `${formatCO2(value)}\n${pct}%`;
        },
        color: "#fff",
        font: { size: 10, weight: "bold" as const },
        textAlign: "center" as const,
      },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.raw as number;
            const pct = total > 0 ? ((val / total) * 100).toFixed(1) : "0";
            return ` ${formatCO2(val)} kg eq.CO₂ (${pct}%)`;
          },
        },
      },
    },
  };
}

const SPLIT_BAR_OPTIONS = {
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: { display: false },
    datalabels: {
      display: true,
      anchor: "end" as const,
      align: "top" as const,
      formatter: (value: number, ctx: any): string => {
        const allData = ctx.chart.data.datasets[0].data as number[];
        const total = allData.reduce((s: number, v: number) => s + (v ?? 0), 0);
        const pct = total > 0 ? ((value / total) * 100).toFixed(1) : "0";
        return `${formatCO2(value)}\n${pct}%`;
      },
      font: { size: 10, weight: "bold" as const },
      color: "#334155",
      textAlign: "center" as const,
    },
    tooltip: {
      callbacks: {
        label: (ctx: any) => {
          const allData = ctx.dataset.data as number[];
          const total = allData.reduce((s: number, v: number) => s + (v ?? 0), 0);
          const pct = total > 0 ? ((ctx.raw / total) * 100).toFixed(1) : "0";
          return ` ${formatCO2(ctx.raw)} kg eq.CO₂ (${pct}%)`;
        },
      },
    },
  },
  scales: {
    x: { grid: { display: false }, ticks: { font: { size: 11 }, color: "#334155" } },
    y: {
      grid: { color: "#f1f5f9" },
      ticks: { font: { size: 11 }, color: "#64748b", callback: (v: any) => formatCO2(Number(v)) },
    },
  },
  layout: { padding: { top: 35 } },
};

// ─── Reusable horizontal bar chart for poste detail ───────────────────────────
interface PosteDetailProps {
  title: string;
  color: string;
  items: CarboneSummaryItem[];
}
const PosteDetail: React.FC<PosteDetailProps> = ({ title, color, items }) => {
  if (!items.length) return null;
  const sorted = [...items].sort((a, b) => b.total_co2 - a.total_co2);
  const chartData = {
    labels: sorted.map((i) => i.name),
    datasets: [
      {
        label: "kg eq.CO₂",
        data: sorted.map((i) => i.total_co2),
        backgroundColor: color + "BB",
        borderColor: color,
        borderWidth: 1,
        borderRadius: 4,
        borderSkipped: false as const,
      },
    ],
  };
  const options = {
    indexAxis: "y" as const,
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${formatCO2(ctx.raw)} kg eq.CO₂`,
        },
      },
    },
    scales: {
      x: {
        grid: { color: "#f1f5f9" },
        ticks: { font: { size: 11 }, color: "#64748b" },
      },
      y: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#334155" },
      },
    },
  };
  const chartHeight = Math.max(120, sorted.length * 36);
  return (
    <div className="poste-detail">
      <div className="poste-detail-header" style={{ borderLeftColor: color }}>
        <span className="poste-detail-title">{title}</span>
        <span className="poste-detail-total" style={{ color }}>
          {formatCO2(sumSummary(items))} kg eq.CO₂
        </span>
      </div>
      <div style={{ height: chartHeight }}>
        <Bar data={chartData} options={options} />
      </div>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────────────
interface CarbonFootprintPageProps {
  user: BackendUser;
}

const CarbonFootprintPage: React.FC<CarbonFootprintPageProps> = ({ user }) => {
  const navigate = useNavigate();
  const handleLogout = () => navigate("/");

  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  // Campaign for carbon: Oct–Sep. If current month >= Oct, campaign starts this year.
  const currentCampaign =
    currentMonth >= 10
      ? `${currentYear % 100}/${(currentYear + 1) % 100}`
      : `${(currentYear - 1) % 100}/${currentYear % 100}`;

  // ── Shared filter state (same keys as EnergyPage / WaterPage) ──
  const [filterType, setFilterType] = usePersistedState<"months" | "campaigns">(
    "shared-filterType",
    "months"
  );
  const [fromPeriod, setFromPeriod] = usePersistedState(
    "shared-fromPeriod",
    `${currentYear}-01`
  );
  const [toPeriod, setToPeriod] = usePersistedState(
    "shared-toPeriod",
    `${currentYear}-12`
  );
  const [fromCampaign, setFromCampaign] = usePersistedState(
    "shared-fromCampaign",
    currentCampaign
  );
  const [toCampaign, setToCampaign] = usePersistedState(
    "shared-toCampaign",
    currentCampaign
  );
  const [selectedSites, setSelectedSites] = usePersistedState<(number | string)[]>(
    "shared-sites", []
  );
  const [selectedBUs, setSelectedBUs] = usePersistedState<(number | string)[]>(
    "shared-bus", []
  );
  const [selectedActivites, setSelectedActivites] = usePersistedState<(number | string)[]>(
    "shared-activites", []
  );
  const [selectedFilieres, setSelectedFilieres] = usePersistedState<(number | string)[]>(
    "shared-filieres", []
  );
  const [selectedRegions, setSelectedRegions] = usePersistedState<(number | string)[]>(
    "shared-regions", []
  );

  const [activeTab, setActiveTab] = useState<ScopeTab>("scope1");

  // ── Campaign helpers (Oct–Sep for carbon) ──
  const campaignToMonths = (campaign: string): { start: string; end: string } => {
    const [startYear, endYear] = campaign.split("/").map((y) => 2000 + parseInt(y));
    return { start: `${startYear}-10`, end: `${endYear}-09` };
  };

  const effectivePeriods = useMemo(() => {
    if (filterType === "campaigns") {
      const fromRange = campaignToMonths(fromCampaign);
      const toRange = campaignToMonths(toCampaign);
      return { from_period: fromRange.start, to_period: toRange.end };
    }
    return { from_period: fromPeriod, to_period: toPeriod };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType, fromPeriod, toPeriod, fromCampaign, toCampaign]);

  const availableCampaigns = useMemo(() => {
    const list: string[] = [];
    for (let y = currentYear - 5; y <= currentYear + 5; y++) {
      list.push(`${y % 100}/${(y + 1) % 100}`);
    }
    return list;
  }, [currentYear]);

  const availableMonths = useMemo(() => {
    const monthNames = [
      "Janvier", "Février", "Mars", "Avril", "Mai", "Juin",
      "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre",
    ];
    const list: { value: string; label: string }[] = [];
    for (let y = currentYear - 5; y <= currentYear + 5; y++) {
      for (let m = 1; m <= 12; m++) {
        list.push({
          value: `${y}-${String(m).padStart(2, "0")}`,
          label: `${monthNames[m - 1]} ${y}`,
        });
      }
    }
    return list;
  }, [currentYear]);

  // Group monthly time-series by campaign (Oct–Sep)
  const groupByCampaign = <T extends Record<string, any>>(timeSeries: T[]) => {
    const groups = new Map<string, T[]>();
    timeSeries.forEach((ts) => {
      const month = ts.month as string;
      const [year, monthNum] = month.split("-").map(Number);
      const campaign =
        monthNum >= 10
          ? `${year % 100}/${(year + 1) % 100}`
          : `${(year - 1) % 100}/${year % 100}`;
      if (!groups.has(campaign)) groups.set(campaign, []);
      groups.get(campaign)!.push(ts);
    });
    return groups;
  };

  // ── Reference data ──
  const { data: sites = [] } = useGetSites();
  const { data: bus = [] } = useGetBUs();
  const { data: activites = [] } = useGetActivites();
  const { data: filieres = [] } = useGetFilieres();
  const { data: regions = [] } = useGetRegions();

  // ── Build filters ──
  const siteIds = selectedSites.length > 0 ? selectedSites.map(Number) : undefined;
  const buIds = selectedBUs.length > 0 ? selectedBUs.map(Number) : undefined;
  const activiteIds = selectedActivites.length > 0 ? selectedActivites.map(Number) : undefined;
  const filiereIds = selectedFilieres.length > 0 ? selectedFilieres.map(Number) : undefined;
  const regionIds = selectedRegions.length > 0 ? selectedRegions.map(Number) : undefined;

  const siteIdsKey = JSON.stringify(siteIds);
  const buIdsKey = JSON.stringify(buIds);
  const activiteIdsKey = JSON.stringify(activiteIds);
  const filiereIdsKey = JSON.stringify(filiereIds);
  const regionIdsKey = JSON.stringify(regionIds);

  const filters: KpisFilters = useMemo(
    () => ({
      from_period: effectivePeriods.from_period,
      to_period: effectivePeriods.to_period,
      ...(siteIds ? { site_ids: siteIds } : {}),
      ...(buIds ? { bu_ids: buIds } : {}),
      ...(activiteIds ? { activite_ids: activiteIds } : {}),
      ...(filiereIds ? { filiere_ids: filiereIds } : {}),
      ...(regionIds ? { region_ids: regionIds } : {}),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      effectivePeriods.from_period, effectivePeriods.to_period,
      siteIdsKey, buIdsKey, activiteIdsKey, filiereIdsKey, regionIdsKey,
    ]
  );

  // ── Fetch all 8 carbone endpoints ──
  const { data: energieData, isLoading: l1 } = useGetEnergieCarboneEmpreinte(filters);
  const { data: elevageData, isLoading: l2 } = useGetElevageCarboneEmpreinte(filters);
  const { data: solData, isLoading: l3 } = useGetSolCarboneEmpreinte(filters);
  const { data: gazFrigoData, isLoading: l4 } = useGetGazFrigoCarboneEmpreinte(filters);
  const { data: transportData, isLoading: l5 } = useGetTransportCarboneEmpreinte(filters);
  const { data: achatData, isLoading: l6 } = useGetAchatCarboneEmpreinte(filters);
  const { data: immoData, isLoading: l7 } = useGetImmobilisationCarboneEmpreinte(filters);
  const { data: dechetsData, isLoading: l8 } = useGetDechetsCarboneEmpreinte(filters);
  const isLoading = l1 || l2 || l3 || l4 || l5 || l6 || l7 || l8;

  // ── Scope totals ──
  const energieSummary = useMemo(() => energieData?.summary ?? [], [energieData?.summary]);

  const scope1Total = useMemo(
    () =>
      energieSummary
        .filter((i) => classifyEnergieScope(i.code) === 1)
        .reduce((s, i) => s + i.total_co2, 0) +
      sumSummary(elevageData?.summary) +
      sumSummary(solData?.summary) +
      sumSummary(gazFrigoData?.summary),
    [energieSummary, elevageData, solData, gazFrigoData]
  );

  const scope2Total = useMemo(
    () =>
      energieSummary
        .filter((i) => classifyEnergieScope(i.code) === 2)
        .reduce((s, i) => s + i.total_co2, 0),
    [energieSummary]
  );

  const scope3Total = useMemo(
    () =>
      energieSummary
        .filter((i) => classifyEnergieScope(i.code) === 3)
        .reduce((s, i) => s + i.total_co2, 0) +
      sumSummary(transportData?.summary) +
      sumSummary(achatData?.summary) +
      sumSummary(immoData?.summary) +
      sumSummary(dechetsData?.summary),
    [energieSummary, transportData, achatData, immoData, dechetsData]
  );

  const grandTotal = scope1Total + scope2Total + scope3Total;

  // ── Donut chart: répartition par poste ──
  const donutData = useMemo(() => {
    const entries = [
      { label: "Énergie",        total: sumSummary(energieData?.summary), color: "#f59e0b" },
      { label: "Élevage",        total: sumSummary(elevageData?.summary), color: "#16a34a" },
      { label: "Sols",           total: sumSummary(solData?.summary),     color: "#84cc16" },
      { label: "Gaz Réfrig.",    total: sumSummary(gazFrigoData?.summary),color: "#06b6d4" },
      { label: "Transport",      total: sumSummary(transportData?.summary),color: "#8b5cf6" },
      { label: "Achats",         total: sumSummary(achatData?.summary),   color: "#ec4899" },
      { label: "Immobilisations",total: sumSummary(immoData?.summary),    color: "#64748b" },
      { label: "Déchets",        total: sumSummary(dechetsData?.summary), color: "#a16207" },
    ].filter((e) => e.total > 0);

    if (!entries.length) return null;
    return {
      labels: entries.map((e) => e.label),
      datasets: [
        {
          data: entries.map((e) => e.total),
          backgroundColor: entries.map((e) => e.color),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 6,
        },
      ],
    };
  }, [energieData, elevageData, solData, gazFrigoData, transportData, achatData, immoData, dechetsData]);

  // ── Stacked bar: évolution par poste (monthly or by campaign) ──
  const monthlyChartData = useMemo(() => {
    const posteMonthly = [
      { label: "Énergie",        ts: energieData?.time_series,   color: "#f59e0b" },
      { label: "Élevage",        ts: elevageData?.time_series,   color: "#16a34a" },
      { label: "Sols",           ts: solData?.time_series,       color: "#84cc16" },
      { label: "Gaz Réfrig.",    ts: gazFrigoData?.time_series,  color: "#06b6d4" },
      { label: "Transport",      ts: transportData?.time_series, color: "#8b5cf6" },
      { label: "Achats",         ts: achatData?.time_series,     color: "#ec4899" },
      { label: "Immobilisations",ts: immoData?.time_series,      color: "#64748b" },
      { label: "Déchets",        ts: dechetsData?.time_series,   color: "#a16207" },
    ];

    if (filterType === "campaigns") {
      const allCampaigns = new Set<string>();
      const posteGrouped: { label: string; color: string; campaignTotals: Map<string, number> }[] = [];

      for (const { label, ts, color } of posteMonthly) {
        if (!ts?.length) continue;
        const monthlyTotals = getMonthlyTotals(ts);
        const groups = groupByCampaign(monthlyTotals);
        const campaignTotals = new Map<string, number>();
        groups.forEach((months, campaign) => {
          allCampaigns.add(campaign);
          campaignTotals.set(campaign, months.reduce((s, m) => s + m.total, 0));
        });
        posteGrouped.push({ label, color, campaignTotals });
      }

      const campaigns = Array.from(allCampaigns).sort();
      if (!campaigns.length) return null;

      const datasets = posteGrouped
        .map(({ label, color, campaignTotals }) => {
          const data = campaigns.map((c) => campaignTotals.get(c) ?? 0);
          if (!data.some((v) => v > 0)) return null;
          return { label, data, backgroundColor: color + "CC", borderRadius: 3, borderSkipped: false as const, stack: "postes" };
        })
        .filter(Boolean) as any[];

      if (!datasets.length) return null;
      return { labels: campaigns, datasets };
    }

    const allMonths = new Set<string>();
    posteMonthly.forEach(({ ts }) =>
      (ts ?? []).forEach((e) => allMonths.add(e.month))
    );
    const months = Array.from(allMonths).sort();
    if (!months.length) return null;

    const datasets = posteMonthly
      .map(({ label, ts, color }) => {
        const monthMap = new Map(
          getMonthlyTotals(ts).map((e) => [e.month, e.total])
        );
        const data = months.map((m) => monthMap.get(m) ?? 0);
        if (!data.some((v) => v > 0)) return null;
        return {
          label,
          data,
          backgroundColor: color + "CC",
          borderRadius: 3,
          borderSkipped: false as const,
          stack: "postes",
        };
      })
      .filter(Boolean) as any[];

    if (!datasets.length) return null;
    return { labels: months, datasets };
  }, [energieData, elevageData, solData, gazFrigoData, transportData, achatData, immoData, dechetsData, filterType]);

  // ── Scope classification of energie summary ──
  const energieScope1Items = energieSummary.filter((i) => classifyEnergieScope(i.code) === 1);
  const energieScope2Items = energieSummary.filter((i) => classifyEnergieScope(i.code) === 2);
  const energieScope3Items = energieSummary.filter((i) => classifyEnergieScope(i.code) === 3);

  // ── Chart options ──
  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "60%",
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: { font: { size: 12 }, padding: 12, boxWidth: 14 },
        onClick: legendIsolateClickPie,
      },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => {
            const val = ctx.raw as number;
            const pct = grandTotal > 0 ? ((val / grandTotal) * 100).toFixed(1) : "0";
            return ` ${formatCO2(val)} kg eq.CO₂  (${pct}%)`;
          },
        },
      },
    },
  };

  const stackedBarOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom" as const,
        labels: { font: { size: 11 }, padding: 10, boxWidth: 12 },
        onClick: legendIsolateClickDataset,
      },
      datalabels: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx: any) => ` ${ctx.dataset.label}: ${formatCO2(ctx.raw)} kg eq.CO₂`,
          footer: (items: any[]) => {
            const total = items.reduce((s: number, i: any) => s + (i.raw ?? 0), 0);
            return `Total: ${formatCO2(total)} kg eq.CO₂`;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
        grid: { display: false },
        ticks: {
          font: { size: 10 },
          color: "#64748b",
          maxRotation: 45,
          minRotation: 0,
        },
      },
      y: {
        stacked: true,
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 11 },
          color: "#64748b",
          callback: (v: any) => formatCO2(Number(v)),
        },
      },
    },
  };

  const hasData = grandTotal > 0;

  // ── Dimension split hooks ──
  const carboneActiviteItems = useMemo(
    () => activites.map((a) => ({ id: a.id, name: a.name })),
    [activites]
  );
  const carboneFiliereItems = useMemo(
    () => filieres.map((f) => ({ id: f.id, name: f.name })),
    [filieres]
  );

  const { data: carboneByActivite, isLoading: loadingActivite } = useGetCarboneByDimension({
    filters,
    dimensionKey: "activite_ids",
    items: carboneActiviteItems,
    enabled: !isLoading && hasData && carboneActiviteItems.length > 0,
  });
  const { data: carboneByFiliere, isLoading: loadingFiliere } = useGetCarboneByDimension({
    filters,
    dimensionKey: "filiere_ids",
    items: carboneFiliereItems,
    enabled: !isLoading && hasData && carboneFiliereItems.length > 0,
  });

  const activiteSplitTotal = useMemo(
    () => carboneByActivite.reduce((s, d) => s + d.total, 0),
    [carboneByActivite]
  );
  const filiereSplitTotal = useMemo(
    () => carboneByFiliere.reduce((s, d) => s + d.total, 0),
    [carboneByFiliere]
  );

  const activitePieData = useMemo(() => {
    if (!carboneByActivite.length) return null;
    return {
      labels: carboneByActivite.map((d) => d.name),
      datasets: [
        {
          data: carboneByActivite.map((d) => d.total),
          backgroundColor: carboneByActivite.map((_, i) => SPLIT_COLORS[i % SPLIT_COLORS.length]),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 8,
        },
      ],
    };
  }, [carboneByActivite]);

  const filierePieData = useMemo(() => {
    if (!carboneByFiliere.length) return null;
    return {
      labels: carboneByFiliere.map((d) => d.name),
      datasets: [
        {
          data: carboneByFiliere.map((d) => d.total),
          backgroundColor: carboneByFiliere.map((_, i) => SPLIT_COLORS[i % SPLIT_COLORS.length]),
          borderWidth: 2,
          borderColor: "#fff",
          hoverOffset: 8,
        },
      ],
    };
  }, [carboneByFiliere]);

  const filiereBarData = useMemo(() => {
    if (!carboneByFiliere.length) return null;
    return {
      labels: carboneByFiliere.map((d) => d.name),
      datasets: [
        {
          label: "kg eq.CO₂",
          data: carboneByFiliere.map((d) => d.total),
          backgroundColor: carboneByFiliere.map(
            (_, i) => SPLIT_COLORS[i % SPLIT_COLORS.length] + "CC"
          ),
          borderColor: carboneByFiliere.map((_, i) => SPLIT_COLORS[i % SPLIT_COLORS.length]),
          borderWidth: 1,
          borderRadius: 6,
          borderSkipped: false as const,
        },
      ],
    };
  }, [carboneByFiliere]);

  const activitePieOptions = useMemo(
    () => makeSplitPieOptions(activiteSplitTotal),
    [activiteSplitTotal]
  );
  const filierePieOptions = useMemo(
    () => makeSplitPieOptions(filiereSplitTotal),
    [filiereSplitTotal]
  );

  return (
    <div className="dashboard-wrapper">
      <div className="dashboard-content">
        <Topbar
          title="Bilan Carbone"
          userName={user?.username || user?.email || "Utilisateur"}
          onLogout={handleLogout}
          user={user}
          sites={sites}
        />

        <div className="carbon-footprint-page">
          <div className="carbon-content">
          {/* ── Header ── */}
          <div className="carbon-page-header">
            <div className="carbon-header-left">
              <div className="carbon-icon-wrapper">
                <Leaf size={26} />
              </div>
              <div>
                <h1 className="carbon-title">Bilan Carbone</h1>
                <p className="carbon-subtitle">Empreinte GES complète par poste d'émission</p>
              </div>
            </div>
            <div className="carbon-header-badge">
              <Leaf size={14} />
              <span>8 postes analysés</span>
            </div>
          </div>

          {/* ── Filter Panel ── */}
          <div className="carbon-card carbon-filter-card">
            <div className="carbon-filter-header">
              <Filter size={16} />
              <span>Filtres</span>
            </div>
            <div className="carbon-filters-grid">
              {/* Period type toggle */}
              <div className="carbon-filter-item">
                <label className="carbon-filter-label">Type de période</label>
                <div className="carbon-period-toggle">
                  {(["months", "campaigns"] as const).map((p) => (
                    <button
                      key={p}
                      className={`carbon-toggle-btn${filterType === p ? " active" : ""}`}
                      onClick={() => setFilterType(p)}
                    >
                      {p === "campaigns" ? "Par Campagne" : "Par Mois"}
                    </button>
                  ))}
                </div>
              </div>

              {filterType === "months" ? (
                <>
                  <div className="carbon-filter-item">
                    <label className="carbon-filter-label">
                      <Calendar size={13} /> Période de début
                    </label>
                    <select
                      className="carbon-filter-input"
                      value={fromPeriod}
                      onChange={(e) => {
                        const newFrom = e.target.value;
                        setFromPeriod(newFrom);
                        if (toPeriod < newFrom) setToPeriod(newFrom);
                      }}
                    >
                      {availableMonths.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                  <div className="carbon-filter-item">
                    <label className="carbon-filter-label">
                      <Calendar size={13} /> Période de fin
                    </label>
                    <select
                      className="carbon-filter-input"
                      value={toPeriod}
                      onChange={(e) => setToPeriod(e.target.value)}
                    >
                      {availableMonths.map((m) => (
                        <option key={m.value} value={m.value}>{m.label}</option>
                      ))}
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="carbon-filter-item">
                    <label className="carbon-filter-label">
                      <Calendar size={13} /> Campagne de début
                    </label>
                    <select
                      className="carbon-filter-input"
                      value={fromCampaign}
                      onChange={(e) => {
                        const newFrom = e.target.value;
                        setFromCampaign(newFrom);
                        const fromIdx = availableCampaigns.indexOf(newFrom);
                        const toIdx = availableCampaigns.indexOf(toCampaign);
                        if (toIdx < fromIdx) setToCampaign(newFrom);
                      }}
                    >
                      {availableCampaigns.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="carbon-filter-item">
                    <label className="carbon-filter-label">
                      <Calendar size={13} /> Campagne de fin
                    </label>
                    <select
                      className="carbon-filter-input"
                      value={toCampaign}
                      onChange={(e) => setToCampaign(e.target.value)}
                    >
                      {availableCampaigns.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}
              {/* Sites */}
              <div className="carbon-filter-item">
                <label className="carbon-filter-label">Sites</label>
                <MultiSearchableCombobox
                  items={sites.map((s) => ({ value: s.id, label: s.name }))}
                  values={selectedSites}
                  onChange={setSelectedSites}
                  placeholder="Tous les sites"
                />
              </div>
              {/* BU */}
              <div className="carbon-filter-item">
                <label className="carbon-filter-label">Business Units</label>
                <MultiSearchableCombobox
                  items={bus.map((b) => ({ value: b.id, label: b.name }))}
                  values={selectedBUs}
                  onChange={setSelectedBUs}
                  placeholder="Toutes les BU"
                />
              </div>
              {/* Activités */}
              <div className="carbon-filter-item">
                <label className="carbon-filter-label">Activités</label>
                <MultiSearchableCombobox
                  items={activites.map((a) => ({ value: a.id, label: a.name }))}
                  values={selectedActivites}
                  onChange={setSelectedActivites}
                  placeholder="Toutes les activités"
                />
              </div>
              {/* Filières */}
              <div className="carbon-filter-item">
                <label className="carbon-filter-label">Filières</label>
                <MultiSearchableCombobox
                  items={filieres.map((f) => ({ value: f.id, label: f.name }))}
                  values={selectedFilieres}
                  onChange={setSelectedFilieres}
                  placeholder="Toutes les filières"
                />
              </div>
              {/* Régions */}
              <div className="carbon-filter-item">
                <label className="carbon-filter-label">Régions</label>
                <MultiSearchableCombobox
                  items={regions.map((r) => ({ value: r.id, label: r.name }))}
                  values={selectedRegions}
                  onChange={setSelectedRegions}
                  placeholder="Toutes les régions"
                />
              </div>
            </div>
          </div>

          {/* ── Loading overlay ── */}
          {isLoading && (
            <div className="carbon-loading">
              <Loader2 size={28} className="carbon-loading-icon" />
              <span>Chargement des données…</span>
            </div>
          )}

          {/* ── KPI Cards ── */}
          <div className="kpi-cards-grid">
            <div className="kpi-card kpi-card--total">
              <div className="kpi-card-header">
                <span className="kpi-label">Émissions totales</span>
                <Leaf size={18} className="kpi-icon-svg" />
              </div>
              <div className="kpi-value green">{formatCO2(grandTotal)}</div>
              <p className="kpi-subtitle">kg eq.CO₂ sur la période</p>
            </div>
            <div className="kpi-card kpi-card--scope1">
              <div className="kpi-card-header">
                <span className="kpi-label">Scope 1</span>
                <span className="kpi-scope-badge scope1">Directs</span>
              </div>
              <div className="kpi-value scope1-color">{formatCO2(scope1Total)}</div>
              <p className="kpi-subtitle">
                Énergie combustion · Élevage · Sols · Gaz Réfrig.
              </p>
              {grandTotal > 0 && (
                <>
                  <div className="kpi-scope-pct">{((scope1Total / grandTotal) * 100).toFixed(1)}%</div>
                  <div className="kpi-progress">
                    <div
                      className="kpi-progress-bar scope1-bar"
                      style={{ width: `${Math.min(100, (scope1Total / grandTotal) * 100).toFixed(1)}%` }}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="kpi-card kpi-card--scope2">
              <div className="kpi-card-header">
                <span className="kpi-label">Scope 2</span>
                <span className="kpi-scope-badge scope2">Énergie achetée</span>
              </div>
              <div className="kpi-value scope2-color">{formatCO2(scope2Total)}</div>
              <p className="kpi-subtitle">Électricité réseau</p>
              {grandTotal > 0 && (
                <>
                  <div className="kpi-scope-pct">{((scope2Total / grandTotal) * 100).toFixed(1)}%</div>
                  <div className="kpi-progress">
                    <div
                      className="kpi-progress-bar scope2-bar"
                      style={{ width: `${Math.min(100, (scope2Total / grandTotal) * 100).toFixed(1)}%` }}
                    />
                  </div>
                </>
              )}
            </div>
            <div className="kpi-card kpi-card--scope3">
              <div className="kpi-card-header">
                <span className="kpi-label">Scope 3</span>
                <span className="kpi-scope-badge scope3">Indirects</span>
              </div>
              <div className="kpi-value scope3-color">{formatCO2(scope3Total)}</div>
              <p className="kpi-subtitle">
                Transport · Achats · Immobilis. · Déchets · Amont
              </p>
              {grandTotal > 0 && (
                <>
                  <div className="kpi-scope-pct">{((scope3Total / grandTotal) * 100).toFixed(1)}%</div>
                  <div className="kpi-progress">
                    <div
                      className="kpi-progress-bar scope3-bar"
                      style={{ width: `${Math.min(100, (scope3Total / grandTotal) * 100).toFixed(1)}%` }}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          {!isLoading && !hasData && (
            <div className="carbon-empty">
              <Leaf size={40} strokeWidth={1.5} />
              <p>Aucune donnée carbone pour les filtres sélectionnés.</p>
              <span>Modifiez la période ou les filtres organisationnels.</span>
            </div>
          )}

          {hasData && (
            <>
              {/* ── Charts Row ── */}
              <div className="charts-2-col">
                {/* Donut */}
                <div className="carbon-card">
                  <div className="carbon-card-header">
                    <h2>Répartition par poste</h2>
                    <p className="carbon-card-description">
                      Distribution des émissions GES — {formatCO2(grandTotal)} kg eq.CO₂
                    </p>
                  </div>
                  <div className="carbon-card-content">
                    {donutData ? (
                      <div className="carbon-donut-wrapper">
                        <Doughnut data={donutData} options={donutOptions} />
                      </div>
                    ) : (
                      <div className="carbon-chart-empty">Aucune donnée</div>
                    )}
                  </div>
                </div>

                {/* Stacked monthly bar */}
                <div className="carbon-card">
                  <div className="carbon-card-header">
                    <h2>Évolution {filterType === "campaigns" ? "par campagne" : "mensuelle"} par poste</h2>
                    <p className="carbon-card-description">kg eq.CO₂ cumulés {filterType === "campaigns" ? "par campagne (Oct–Sep)" : "par mois"}</p>
                  </div>
                  <div className="carbon-card-content">
                    {monthlyChartData ? (
                      <div className="carbon-bar-wrapper">
                        <Bar data={monthlyChartData} options={stackedBarOptions} />
                      </div>
                    ) : (
                      <div className="carbon-chart-empty">Aucune donnée mensuelle</div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── Dimension Splits ── */}
              {(carboneActiviteItems.length > 0 || carboneFiliereItems.length > 0) && (
                <>
                  <div className="charts-2-col">
                    {carboneActiviteItems.length > 0 && (
                      <div className="carbon-card">
                        <div className="carbon-card-header">
                          <h2>Activité Split (kg eq.CO₂)</h2>
                          <p className="carbon-card-description">
                            Répartition des émissions GES par activité
                          </p>
                        </div>
                        <div className="carbon-card-content">
                          {loadingActivite ? (
                            <div className="carbon-split-loading">
                              <Loader2 size={22} className="carbon-loading-icon" />
                            </div>
                          ) : activitePieData ? (
                            <div className="carbon-pie-wrapper">
                              <Doughnut data={activitePieData} options={activitePieOptions} />
                            </div>
                          ) : (
                            <div className="carbon-chart-empty">Aucune donnée par activité</div>
                          )}
                        </div>
                      </div>
                    )}
                    {carboneFiliereItems.length > 0 && (
                      <div className="carbon-card">
                        <div className="carbon-card-header">
                          <h2>Filière Split (kg eq.CO₂)</h2>
                          <p className="carbon-card-description">
                            Répartition des émissions GES par filière
                          </p>
                        </div>
                        <div className="carbon-card-content">
                          {loadingFiliere ? (
                            <div className="carbon-split-loading">
                              <Loader2 size={22} className="carbon-loading-icon" />
                            </div>
                          ) : filierePieData ? (
                            <div className="carbon-pie-wrapper">
                              <Doughnut data={filierePieData} options={filierePieOptions} />
                            </div>
                          ) : (
                            <div className="carbon-chart-empty">Aucune donnée par filière</div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {!loadingFiliere && filiereBarData && (
                    <div className="carbon-card">
                      <div className="carbon-card-header">
                        <h2>Filière Split — Détail</h2>
                        <p className="carbon-card-description">
                          Émissions GES par filière — kg eq.CO₂
                        </p>
                      </div>
                      <div className="carbon-card-content">
                        <div className="carbon-split-bar-wrapper">
                          <Bar data={filiereBarData} options={SPLIT_BAR_OPTIONS} />
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}

              {/* ── Scope Details Tabs ── */}
              <div className="carbon-card">
                <div className="carbon-card-header">
                  <h2>Détails par Scope</h2>
                  <p className="carbon-card-description">Sources d'émissions détaillées par catégorie GHG</p>
                </div>
                <div className="carbon-card-content">
                  <div className="tabs-container">
                    <div className="tabs-list">
                      {(
                        [
                          { id: "scope1", label: "Scope 1", sublabel: "Directs", color: "#16a34a" },
                          { id: "scope2", label: "Scope 2", sublabel: "Énergie achetée", color: "#3b82f6" },
                          { id: "scope3", label: "Scope 3", sublabel: "Indirects", color: "#f59e0b" },
                        ] as const
                      ).map((tab) => (
                        <button
                          key={tab.id}
                          className={`tab-trigger ${activeTab === tab.id ? "active" : ""}`}
                          onClick={() => setActiveTab(tab.id)}
                          style={
                            activeTab === tab.id ? { borderBottomColor: tab.color, color: tab.color } : {}
                          }
                        >
                          <span>{tab.label}</span>
                          <span className="tab-sublabel">{tab.sublabel}</span>
                        </button>
                      ))}
                    </div>

                    <div className="tab-content">
                      {/* ── Scope 1 ── */}
                      {activeTab === "scope1" && (
                        <div className="tab-pane">
                          <div className="scope-intro scope1-intro">
                            <div className="scope-dot scope1-dot" />
                            <span>Émissions directes issues des activités contrôlées par l'entreprise</span>
                          </div>
                          <div className="scope-details-grid">
                            <PosteDetail
                              title="Énergie — Combustion directe"
                              color="#f59e0b"
                              items={energieScope1Items}
                            />
                            <PosteDetail
                              title="Élevage"
                              color="#16a34a"
                              items={elevageData?.summary ?? []}
                            />
                            <PosteDetail
                              title="Sols"
                              color="#84cc16"
                              items={solData?.summary ?? []}
                            />
                            <PosteDetail
                              title="Gaz Réfrigérants"
                              color="#06b6d4"
                              items={gazFrigoData?.summary ?? []}
                            />
                          </div>
                          <div className="scope-total-banner scope1-banner">
                            <span>Total Scope 1</span>
                            <strong>{formatCO2(scope1Total)} kg eq.CO₂</strong>
                          </div>
                        </div>
                      )}

                      {/* ── Scope 2 ── */}
                      {activeTab === "scope2" && (
                        <div className="tab-pane">
                          <div className="scope-intro scope2-intro">
                            <div className="scope-dot scope2-dot" />
                            <span>Émissions indirectes liées à la consommation d'énergie achetée</span>
                          </div>
                          <div className="scope-details-grid">
                            <PosteDetail
                              title="Énergie — Électricité réseau"
                              color="#3b82f6"
                              items={energieScope2Items}
                            />
                          </div>
                          {energieScope2Items.length === 0 && (
                            <div className="carbon-chart-empty" style={{ marginTop: 24 }}>
                              Aucune donnée d'électricité pour la période sélectionnée.
                            </div>
                          )}
                          <div className="scope-total-banner scope2-banner">
                            <span>Total Scope 2</span>
                            <strong>{formatCO2(scope2Total)} kg eq.CO₂</strong>
                          </div>
                        </div>
                      )}

                      {/* ── Scope 3 ── */}
                      {activeTab === "scope3" && (
                        <div className="tab-pane">
                          <div className="scope-intro scope3-intro">
                            <div className="scope-dot scope3-dot" />
                            <span>Autres émissions indirectes de la chaîne de valeur</span>
                          </div>
                          <div className="scope-details-grid">
                            {energieScope3Items.length > 0 && (
                              <PosteDetail
                                title="Énergie — Émissions amont"
                                color="#f59e0b"
                                items={energieScope3Items}
                              />
                            )}
                            <PosteDetail
                              title="Transport"
                              color="#8b5cf6"
                              items={transportData?.summary ?? []}
                            />
                            <PosteDetail
                              title="Achats"
                              color="#ec4899"
                              items={achatData?.summary ?? []}
                            />
                            <PosteDetail
                              title="Immobilisations"
                              color="#64748b"
                              items={immoData?.summary ?? []}
                            />
                            <PosteDetail
                              title="Déchets"
                              color="#a16207"
                              items={dechetsData?.summary ?? []}
                            />
                          </div>
                          <div className="scope-total-banner scope3-banner">
                            <span>Total Scope 3</span>
                            <strong>{formatCO2(scope3Total)} kg eq.CO₂</strong>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </>
          )}
          </div>{/* carbon-content */}
        </div>
      </div>
    </div>
  );
};

export default CarbonFootprintPage;
